import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  Clock,
  Loader2,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import api from "../lib/api";
import {
  WhatWeDoSection,
  IndustriesSection,
  EngagementModelsSection,
  TechnologiesSection,
  FAQSection,
} from "../components/CommonSections";

const API_HOST = process.env.REACT_APP_API_HOST || "http://127.0.0.1:8000";

// Blogs shown per page in the grid.
const PAGE_SIZE = 12;

// Resolve a stored image path to a full URL.
const imageUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_HOST.replace(/\/$/, "")}/${String(path).replace(/^\/?/, "")}`;
};

// Plain-text excerpt from the rich HTML description.
const excerptOf = (html, length = 160) => {
  if (!html) return "";
  const text = String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > length ? `${text.slice(0, length).trim()}…` : text;
};

// Estimated reading time from the rich HTML description (~200 wpm).
const readTimeOf = (html) => {
  if (!html) return "";
  const words = String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .split(/\s+/)
    .filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isActive = (blog) =>
  blog.is_active === true ||
  blog.is_active === 1 ||
  blog.is_active === "1" ||
  blog.is_active === "active" ||
  blog.is_active === undefined; // show if API doesn't send the flag

const BlogsPage = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [categories, setCategories] = useState(["All"]);
  const [blogs, setBlogs] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setPage(1); // back to the first page when the category changes
      const params =
        activeCategory === "All" ? {} : { category: activeCategory };

      try {
        const [listRes, featRes] = await Promise.all([
          api.get("users/by-category", { params }),
          api.get("/featured", { params }),
        ]);
        if (cancelled) return;

        // List — flat array for a category, grouped object for "All".
        const listData = listRes.data?.data;
        let list = [];
        if (Array.isArray(listData)) {
          list = listData;
        } else if (listData && typeof listData === "object") {
          list = Object.values(listData).flat();
          if (activeCategory === "All") {
            setCategories(["All", ...Object.keys(listData)]);
          }
        }

        // On "All" the API groups blogs by category, so flattening leaves them
        // category-ordered. Sort newest-first instead, ignoring category.
        if (activeCategory === "All") {
          list = [...list].sort(
            (a, b) =>
              new Date(b.blog_date || b.created_at || 0) -
              new Date(a.blog_date || a.created_at || 0)
          );
        }

        setBlogs(list.filter(isActive));

        // Featured — single blog for a category, array (one per category) otherwise.
        const featData = featRes.data?.data;
        setFeatured(
          Array.isArray(featData) ? featData[0] || null : featData || null
        );
      } catch (error) {
        if (!cancelled) {
          setBlogs([]);
          setFeatured(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [activeCategory]);

  // Don't repeat the featured blog inside the grid.
  const gridBlogs = featured
    ? blogs.filter((b) => b.blog_slug !== featured.blog_slug)
    : blogs;

  // Paginate the grid (12 per page).
  const totalPages = Math.max(1, Math.ceil(gridBlogs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedBlogs = gridBlogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const goToPage = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-[#b32126]/20 text-[#ff6b6b] rounded-full text-sm font-semibold mb-6">
              {categories.filter((c) => c !== "All").join(", ") ||
                "News, Events & Resources"}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Insights & Updates
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Stay updated with the latest news, events, and resources from
              Infotech.Works. Discover insights on technology trends, company
              updates, and industry best practices.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter Tabs */}
      <section className="py-8 bg-gray-50 border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2.5 rounded-full font-medium transition-all ${
                  activeCategory === category
                    ? "bg-[#b32126] text-white shadow-lg"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading articles…
            </div>
          ) : (
            <>
              {/* Featured Blog */}
              {featured && (
                <div className="mb-12">
                  <Link
                    to={`/resources/blogs/${featured.blog_slug}`}
                    className="group block"
                    data-testid="featured-read-more"
                  >
                    <div className="grid lg:grid-cols-2 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden border border-gray-100">
                      {/* Image */}
                      <div className="relative bg-gray-100">
                        {featured.blog_image ? (
                          <img
                            src={imageUrl(featured.blog_image)}
                            alt={featured.blog_title}
                            className="w-full h-auto object-contain"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-gray-300">
                            <ImageIcon className="h-10 w-10" />
                          </div>
                        )}
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className="bg-[#b32126] text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                            Featured
                          </span>
                          {featured.blog_category && (
                            <span className="bg-white/95 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                              {featured.blog_category}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Content — on desktop it auto-fits the image's height:
                          the title is pinned on top, the excerpt fills whatever
                          space is left and fades out softly at the bottom (no
                          mid-line cut, no hard-coded length), footer pinned below. */}
                      <div className="lg:relative">
                        <div className="p-6 pt-4 lg:p-10 lg:pt-6 lg:absolute lg:inset-0 flex flex-col">
                          <h2 className="text-xl lg:text-3xl font-bold text-gray-900 mb-4 leading-tight group-hover:text-[#b32126] transition-colors">
                            {featured.blog_title}
                          </h2>
                          <p className="text-gray-600 text-base lg:text-lg leading-relaxed mb-6 max-h-40 overflow-hidden lg:max-h-none lg:flex-1 lg:min-h-0 [mask-image:linear-gradient(to_bottom,black_82%,transparent)] [-webkit-mask-image:linear-gradient(to_bottom,black_82%,transparent)]">
                            {excerptOf(featured.blog_description, Infinity)}
                          </p>

                          {/* Footer — Read Full Article on the left, date & read
                              time on the right, separated by a divider. */}
                          <div className="mt-auto flex flex-wrap items-center justify-between gap-x-5 gap-y-2 pt-4 border-t border-gray-100 text-sm text-gray-500">
                            <span className="inline-flex items-center font-semibold text-[#b32126]">
                              Read Full Article
                              <ArrowRight className="ml-1.5 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                              <span className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1.5" />
                                {formatDate(
                                  featured.blog_date || featured.created_at
                                )}
                              </span>
                              <span className="flex items-center">
                                <Clock className="h-4 w-4 mr-1.5" />
                                {readTimeOf(featured.blog_description)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Results Count */}
              <div className="mb-8">
                <p className="text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-900">
                    {gridBlogs.length}
                  </span>{" "}
                  articles
                  {activeCategory !== "All" && (
                    <span>
                      {" "}
                      in{" "}
                      <span className="font-semibold text-[#b32126]">
                        {activeCategory}
                      </span>
                    </span>
                  )}
                </p>
              </div>

              {/* Blog Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {pagedBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/resources/blogs/${blog.blog_slug}`}
                    className="group"
                    data-testid={`blog-card-${blog.id}`}
                  >
                    <article className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 h-full flex flex-col">
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={imageUrl(blog.blog_image)}
                          alt={blog.blog_title}
                          className="w-full h-full object-containt transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/95 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                            {blog.blog_category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#b32126] transition-colors line-clamp-2">
                          {blog.blog_title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-1">
                          {excerptOf(blog.blog_description)}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                          <span>{formatDate(blog.blog_date || blog.created_at)}</span>
                          <span>{readTimeOf(blog.blog_description)}</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-1">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                    className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => goToPage(p)}
                      className={`h-10 w-10 rounded-md text-sm font-medium transition-colors ${
                        p === currentPage
                          ? "bg-[#b32126] text-white shadow"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                    className="inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Empty State */}
              {blogs.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-gray-500 text-lg">
                    No articles found in this category.
                  </p>
                  {activeCategory !== "All" && (
                    <button
                      onClick={() => setActiveCategory("All")}
                      className="mt-4 text-[#b32126] font-semibold hover:underline"
                    >
                      View all articles
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Common Sections */}
      <WhatWeDoSection />
      <IndustriesSection />
      <TechnologiesSection />
      <EngagementModelsSection bgColor="bg-white" />

      <FAQSection />

      {/* CTA Section */}
      <section className="py-16 bg-[#b32126]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help bring your ideas to life.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#b32126] font-semibold rounded-xl hover:bg-gray-100 transition-all shadow-lg"
            data-testid="cta-contact"
          >
            Get In Touch
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogsPage;
